/**
 * The "Contact Details" section of the address screen.
 *
 * A picker appears above the fields only when the address book holds more than
 * one contact for this location — with one, it has already been filled in and
 * there is nothing to choose.
 */

import ContactsInput from "@/Shared/Contacts/Inputs/ContactsInput";
import ContactQuestionBubble from "@/Shared/Contacts/Bubbles/ContactQuestionBubble";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import BookShipmentSavedContactsDropdown from "../../Dropdowns/BookShipmentSavedContactsDropdown";
import BookShipmentSmallSection from "../BookShipmentSmallSection";
import type { useBookShipmentAddressContact } from "../../Hooks/useBookShipmentAddressContact";
import type { useBookShipmentAddressValidation } from "../../Hooks/useBookShipmentAddressValidation";

interface BookShipmentAddressContactSectionProps {
  index: number;
  context: LocationContext;
  displayName: string;
  shipment: PreBookingShipment;
  matchingSavedLocation: SavedLocation | undefined;
  forceValidation: boolean;
  addressContact: ReturnType<typeof useBookShipmentAddressContact>;
  validation: ReturnType<typeof useBookShipmentAddressValidation>;
}

export function BookShipmentAddressContactSection(
  props: BookShipmentAddressContactSectionProps,
) {
  const {
    context,
    displayName,
    shipment,
    matchingSavedLocation,
    addressContact,
    validation,
  } = props;

  return (
    <BookShipmentSmallSection
      header={
        <>
          <>Contact Details</>
          <ContactQuestionBubble displayName={displayName} context={context} />
        </>
      }
      description={`Enter the ${
        context === LocationContext.Pickup ? `shipper's` : `receiver's`
      } contact details`}
      index={props.index}
    >
      {addressContact.savedContactId &&
        matchingSavedLocation &&
        matchingSavedLocation.contacts.length > 1 && (
          <>
            <div className="h-4" />
            <BookShipmentSavedContactsDropdown
              savedContactId={addressContact.savedContactId}
              onSetSavedContactId={addressContact.selectSavedContact}
              savedContacts={matchingSavedLocation.contacts}
            />
            <div className="h-8" />
          </>
        )}
      <Panel collapseOnNarrow>
        <ContactsInput
          contact={addressContact.contact}
          contactChanged={addressContact.updateContact}
          forceValidation={props.forceValidation}
          errorMessages={validation.contactErrors}
          warningMessages={validation.contactWarnings}
        />
        {context === LocationContext.Delivery &&
          shipment.additionalNotificationEmails &&
          shipment.additionalNotificationEmails.length > 0 && (
            <>
              <div className="mt-2 text-sm text-content-muted">
                Notifications about this booking will also be sent to these
                additional email addresses:{" "}
                {shipment.additionalNotificationEmails.join(", ")}
              </div>
              <div className="h-4" />
            </>
          )}
      </Panel>
    </BookShipmentSmallSection>
  );
}
